/**
 * Pure payment-terms reference helpers (sb_enumeration ↔ MV API ↔ display label).
 * Runtime maps are built in megaventory-payment-terms-labels.ts from the database.
 */

export type PaymentTermsReferenceMaps = {
  sbToDisplayLabel: Record<string, string>;
  sbToMegaventoryApi: Record<string, string>;
  /** Prefer sb where sb_enumeration equals the MV API value (Net30 → Net30, not PaymentInAdvance). */
  megaventoryApiToSb: Record<string, string>;
  /** Legacy rows: primary key mv_enumeration before sb column existed (same as sb today). */
  legacyRowKeyToSb: Record<string, string>;
};

export const FALLBACK_SB_TO_DISPLAY_LABEL: Record<string, string> = {
  CustomPaymentTerms1: "Payment In Advance",
  CustomPaymentTerms2: "Net45",
  None: "Payment In Advance",
  Net10: "Net 10",
  Net15: "Net 15",
  Net30: "Net 30",
  Net60: "Net 180",
  Net90: "Net 90",
  DueOnReceipt: "Due on receipt",
  OtherPaymentTerms: "Other payment terms",
  Net45: "Net 45",
  Net180: "Net 180",
  PaymentInAdvance: "Payment in advance",
  Other: "Other payment terms",
  "T/T": "T/T",
};

export const FALLBACK_SB_TO_MEGAVENTORY_API: Record<string, string> = {
  CustomPaymentTerms1: "CustomPaymentTerms1",
  CustomPaymentTerms2: "CustomPaymentTerms2",
  None: "None",
  Net10: "Net10",
  Net15: "Net15",
  Net30: "Net30",
  Net60: "Net60",
  Net90: "Net90",
  DueOnReceipt: "DueOnReceipt",
  OtherPaymentTerms: "OtherPaymentTerms",
  Net45: "CustomPaymentTerms2",
  Net180: "None",
  PaymentInAdvance: "CustomPaymentTerms1",
  Other: "OtherPaymentTerms",
  "T/T": "None",
};

export function buildPaymentTermsReferenceMaps(rows: {
  sb_enumeration: string;
  mv_enumeration: string;
  display_label: string;
  megaventory_api_enumeration: string;
}[]): PaymentTermsReferenceMaps {
  const sbToDisplayLabel: Record<string, string> = {};
  const sbToMegaventoryApi: Record<string, string> = {};
  const legacyRowKeyToSb: Record<string, string> = {};
  const megaventoryApiToSbCandidates = new Map<string, string[]>();

  for (const row of rows) {
    const sb = row.sb_enumeration.trim();
    sbToDisplayLabel[sb] = row.display_label;
    sbToMegaventoryApi[sb] = row.megaventory_api_enumeration;
    legacyRowKeyToSb[row.mv_enumeration.trim()] = sb;
    const mvApi = row.megaventory_api_enumeration.trim();
    const list = megaventoryApiToSbCandidates.get(mvApi) ?? [];
    list.push(sb);
    megaventoryApiToSbCandidates.set(mvApi, list);
  }

  const megaventoryApiToSb: Record<string, string> = {};
  for (const [mvApi, candidates] of megaventoryApiToSbCandidates) {
    const preferred = candidates.find((sb) => sb === mvApi);
    megaventoryApiToSb[mvApi] = preferred ?? candidates[0] ?? mvApi;
  }

  return {
    sbToDisplayLabel,
    sbToMegaventoryApi,
    megaventoryApiToSb,
    legacyRowKeyToSb,
  };
}

export function fallbackPaymentTermsReferenceMaps(): PaymentTermsReferenceMaps {
  const rows = Object.keys(FALLBACK_SB_TO_DISPLAY_LABEL).map((sb) => ({
    sb_enumeration: sb,
    mv_enumeration: sb,
    display_label: FALLBACK_SB_TO_DISPLAY_LABEL[sb] ?? sb,
    megaventory_api_enumeration: FALLBACK_SB_TO_MEGAVENTORY_API[sb] ?? "None",
  }));
  return buildPaymentTermsReferenceMaps(rows);
}

function normalizeStoredPaymentTermsInput(input: string | null | undefined): string {
  if (input === null || input === undefined) {
    return "None";
  }
  const trimmed = String(input).trim();
  return trimmed === "" ? "None" : trimmed;
}

/** Resolve user or stored text to canonical sb_enumeration (dual-read: sb, legacy row key, MV API). */
export function sbEnumerationFromPaymentTermsInput(
  input: string | null | undefined,
  maps: PaymentTermsReferenceMaps,
): string {
  const t = normalizeStoredPaymentTermsInput(input);
  if (Object.prototype.hasOwnProperty.call(maps.sbToDisplayLabel, t)) {
    return t;
  }
  const fromLegacy = maps.legacyRowKeyToSb[t];
  if (fromLegacy != null) {
    return fromLegacy;
  }
  const fromMvApi = maps.megaventoryApiToSb[t];
  if (fromMvApi != null) {
    return fromMvApi;
  }
  const labelMatch = Object.entries(maps.sbToDisplayLabel).find(([, label]) => label === t);
  if (labelMatch) {
    return labelMatch[0];
  }
  return t;
}

/**
 * Blank purchase-order terms display as None. Enumerations that share a label
 * (None and CustomPaymentTerms1 are both "Payment In Advance") are the same terms.
 */
export function purchaseOrderPaymentTermsDifferFromSupplier(
  paymentTerms: string | null | undefined,
  supplierPaymentTerms: string | null | undefined,
  enumerationToLabel: Record<string, string>,
): boolean {
  const supplier = (supplierPaymentTerms ?? "").trim();
  if (supplier === "") return false;
  const purchaseOrderTerms = (paymentTerms ?? "").trim() || "None";
  if (purchaseOrderTerms === supplier) return false;
  const purchaseOrderLabel = (enumerationToLabel[purchaseOrderTerms] ?? purchaseOrderTerms).trim().toLowerCase();
  const supplierLabel = (enumerationToLabel[supplier] ?? supplier).trim().toLowerCase();
  return purchaseOrderLabel !== supplierLabel;
}

export function paymentTermsDisplayLabelFromSb(
  sbOrLegacy: string | null | undefined,
  maps: PaymentTermsReferenceMaps,
): string | undefined {
  if (sbOrLegacy === null || sbOrLegacy === undefined) {
    return undefined;
  }
  const sb = sbEnumerationFromPaymentTermsInput(sbOrLegacy, maps);
  return maps.sbToDisplayLabel[sb] ?? (String(sbOrLegacy).trim() === "" ? undefined : String(sbOrLegacy).trim());
}

export function megaventoryApiEnumerationFromSbPaymentTerms(
  sbOrLegacy: string | null | undefined,
  maps: PaymentTermsReferenceMaps,
): string {
  const sb = sbEnumerationFromPaymentTermsInput(sbOrLegacy, maps);
  const mapped = maps.sbToMegaventoryApi[sb];
  if (mapped != null && mapped.trim() !== "") {
    return mapped;
  }
  if (Object.prototype.hasOwnProperty.call(maps.megaventoryApiToSb, sb)) {
    return sb;
  }
  return "None";
}

export function sbEnumerationFromMegaventoryApiEnumeration(
  mvApi: string | null | undefined,
  maps: PaymentTermsReferenceMaps,
): string | null {
  if (mvApi === null || mvApi === undefined) {
    return null;
  }
  const trimmed = String(mvApi).trim();
  if (trimmed === "") {
    return null;
  }
  return maps.megaventoryApiToSb[trimmed] ?? trimmed;
}
